import { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'

import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'
import { ButtonSecondary } from '~/components/Buttons/ButtonSecondary'
import WarningModal from '~/components/WarningModal'
import { Tab } from '~/settings-scenario/types'
import { useDefaultSettingsFields } from '~/settings-scenario/hooks/useDefaultSettingsFields'
import { useUpdateSettings } from '~/settings-scenario/hooks/useUpdateSettings'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import userSettingsAnalytics from '~/analytics/userSettings/userSettingsAnalytics'
import { SETTINGS_LABEL_BY_TAB } from '~/analytics/userSettings/userSettingsAnalytics.const'
import { useResetGradesMutation } from '~/curriculum/apis/curriculum.api'
import { UserField } from '~/types/user'
import { setLockRedirectionActionCreator } from '~/curriculum/slice/redirection.slice'
import { setCurriculumChangeLoadingActionCreator } from '~/curriculum/slice/curriculum.slice'
import { useGetUserChosenCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { isDisplayOnlyCurriculumChange } from '~/curriculum/constants/usCurriculumStates.const'

import { selectFields, selectTab, setFieldValueActionCreator, setFieldsActionCreator } from '../redux/settings.slice'

import { SuccessMessage } from './SuccessMessage'

export default function UserUpdateButtons({
  hasRevertButton = false,
  isInNicknameTab = false,
}: {
  hasRevertButton?: boolean
  isInNicknameTab?: boolean
}): React.ReactElement {
  const dispatch = useAppDispatch()
  const { t } = useTranslation('settings')
  const { defaultFields } = useDefaultSettingsFields()
  const field = useAppSelector(selectFields)
  const handleOnSubmit = useUpdateSettings()
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [resetGrades] = useResetGradesMutation()
  const tab = useAppSelector(selectTab)
  const usersCurriculumCountryCode = useGetUserChosenCurriculumCountryCode()

  useEffect(() => {
    setShowSuccessMessage(false)
  }, [tab])

  const chosenTab = isInNicknameTab || tab === null ? Tab.AVATAR_NICKNAME : tab

  const handleSaveButtonClick = async (): Promise<boolean> => {
    // To enable the redirection from https://github.com/houseofmath/hom-frontend/blob/3caf7c8347a6d543e9aa85edcec17667fa6f9d12/pages/_app.tsx#L61
    dispatch(setLockRedirectionActionCreator(false))

    if (tab === Tab.PROFILE && defaultFields.curriculum !== field.curriculum) {
      if (isDisplayOnlyCurriculumChange(field.curriculum, usersCurriculumCountryCode)) {
        // Continue without showing warning
      } else if (showWarning) {
        dispatch(setCurriculumChangeLoadingActionCreator(true))
        const resetGradeSuccess = await resetGrades().then((res) => {
          return res
        })
        if (!resetGradeSuccess) {
          dispatch(setCurriculumChangeLoadingActionCreator(false))
          return false
        }
      } else {
        setShowWarning(true)
        return true
      }
    }
    userSettingsAnalytics.logSaveSettings(SETTINGS_LABEL_BY_TAB[chosenTab])

    setIsLoading(true)

    const userUpdateSucceeded = await handleOnSubmit(chosenTab)

    if (userUpdateSucceeded) {
      setShowSuccessMessage(true)

      setTimeout(() => {
        setShowSuccessMessage(false)
      }, 3000)
    }

    setIsLoading(false)
    setShowWarning(false)
    dispatch(setCurriculumChangeLoadingActionCreator(false))
    return userUpdateSucceeded
  }

  const handleRevertButtonClick = (): void => {
    dispatch(setFieldsActionCreator(defaultFields))

    userSettingsAnalytics.logRevertSetting(SETTINGS_LABEL_BY_TAB[chosenTab])
  }
  const handleCancelAndRevertCurriculumCountry = (): void => {
    dispatch(setFieldValueActionCreator({ fieldName: UserField.CURRICULUM, value: defaultFields.curriculum }))
    dispatch(setCurriculumChangeLoadingActionCreator(false))
    setShowWarning(false)
  }

  const handleCurriculumChangeConfirm = async (): Promise<void> => {
    await handleSaveButtonClick()
    // Don't return the boolean value, just execute the function
  }

  return (
    <div className="mt-8 w-full space-y-4 sm:space-y-4 justify-self-end flex flex-col sm:flex-col justify-between">
      {showSuccessMessage && <SuccessMessage text={t('successMessage')} />}
      {showWarning && (
        <WarningModal
          cancelOnClick={handleCancelAndRevertCurriculumCountry}
          continueOnClick={handleCurriculumChangeConfirm}
          i18nKey="curriculum:curriculumChangeWarning"
          i18nNamespace="curriculum"
          isChangeLoading={isLoading}
        />
      )}
      <ButtonPrimary isFullWidth isLoading={isLoading} isWide onClick={() => handleSaveButtonClick()} size="small">
        <div className="text-sm font-bold">{t('saveButton')}</div>
      </ButtonPrimary>

      {hasRevertButton && (
        <ButtonSecondary isFullWidth isWide onClick={handleRevertButtonClick} size="small">
          <div className="text-sm">{t('revertButton')}</div>
        </ButtonSecondary>
      )}
    </div>
  )
}
